import Tippy from '@tippy.js/react';
import React, { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { GeneralDialog } from 'sharedComponents';
import { getLocalizedAdvisoryValue } from './CautionModal';
import { IconInfo } from 'svgIcons/MotionPortalIcons';
import {
    ADVISORY_LEVEL_CAUTION,
    CAUTION_BADGE_CLASS_CAUTION,
    CAUTION_BADGE_CLASS_INFO,
    INFO_BADGE_LABEL_DEFAULT,
    CAUTION_LABEL_DEFAULT,
    ADVISORY_ICON_INFO_COLOR,
    ADVISORY_VALUE_TYPE_OBJECT,
    ADVISORY_VALUE_TYPE_STRING,
} from './constants';
import './CautionModal.scss';
import './CautionBadge.scss';


const getLocalizedAdvisoryList = (value, t, i18n) => {
    const items = Array.isArray(value)
        ? value
        : value
            ? [value]
            : [];

    return items
        .map((item) => getLocalizedAdvisoryValue(item, t, i18n))
        .filter(Boolean);
};

const getModalTitle = (levelLabel, title) => {
    const normalizedLevel = String(levelLabel || '').trim();
    const normalizedTitle = String(title || '').trim();

    return [normalizedLevel, normalizedTitle].filter(Boolean).join(': ');
};

const renderKnownLimitation = (item, key) =>
    <li key={key} className='caution-modal-limitation-item'>
        {item}
    </li>;


const CautionBadge = ({ advisory }) => {
    const { t, i18n } = useTranslation();
    const [show, setShow] = useState(false);
    const anchorRef = useRef();

    const handleOpen = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setShow(true);
    }, []);
    const handleClose = useCallback((e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        setShow(false);
    }, []);

    if (!advisory) {
        return null;
    }

    const levelValue = typeof advisory === ADVISORY_VALUE_TYPE_OBJECT && advisory.level ? typeof advisory.level === ADVISORY_VALUE_TYPE_OBJECT ? advisory.level.displayName : advisory.level : '';
    const isCaution = String(levelValue || '').toLowerCase() === ADVISORY_LEVEL_CAUTION;
    const localizedLevelLabel = getLocalizedAdvisoryValue(advisory.level, t, i18n);
    const infoLabel = t('ABB.Powertrain.Frontend.detailedInfoEventLogInfoTitle', { defaultValue: INFO_BADGE_LABEL_DEFAULT });
    const ariaLabel = isCaution ? localizedLevelLabel || CAUTION_LABEL_DEFAULT : infoLabel;
    const badgeClassName = isCaution ? CAUTION_BADGE_CLASS_CAUTION : CAUTION_BADGE_CLASS_INFO;
    const title = getLocalizedAdvisoryValue(advisory.title, t, i18n);
    const modalTitle = getModalTitle(localizedLevelLabel, title);
    const summary = getLocalizedAdvisoryValue(advisory.summary, t, i18n);
    const knownLimitationsTitle = getLocalizedAdvisoryValue(advisory.knownLimitations, t, i18n);
    const knownLimitations = getLocalizedAdvisoryList(advisory.knownLimitations?.displayNames, t, i18n);
    const links = Array.isArray(advisory.links) ? advisory.links : [];
    const titleNode =
        <div className='caution-modal-title'>
            <IconInfo width={24} height={24} color={ADVISORY_ICON_INFO_COLOR} className='caution-modal-title-icon' />
            <span className='caution-modal-title-text'>{modalTitle}</span>
        </div>;

    return (
        <>
            <Tippy content={ariaLabel} theme='light-border'>
                <span>
                    <button
                        type='button'
                        className={badgeClassName}
                        onClick={handleOpen}
                        onMouseDown={e => e.stopPropagation()}
                        aria-label={ariaLabel}
                        ref={anchorRef}
                    >
                        <IconInfo width={13} height={13} color={ADVISORY_ICON_INFO_COLOR} />
                    </button>
                </span>
            </Tippy>
            {show &&
                <GeneralDialog
                    show={show}
                    close={handleClose}
                    title={titleNode}
                    notificationType='default'
                    dialogClassName='caution-modal-dialog'
                    closeButton={true}
                    persistent={false}
                    acceptButtonProps={{
                        text: 'Close',
                        onClick: handleClose
                    }}
                >
                    <div className='caution-modal-body'>
                        {summary && <p className='caution-modal-summary'>{summary}</p>}
                        {knownLimitations.length > 0 &&
                            <div className='caution-modal-limitations'>
                                {knownLimitationsTitle && <p className='caution-modal-summary'>{knownLimitationsTitle}</p>}
                                <ul className='caution-modal-limitation-list'>
                                    {knownLimitations.map((item, i) => renderKnownLimitation(item, `${item}-${i}`))}
                                </ul>
                            </div>
                        }
                        {links.length > 0 &&
                            <div className='caution-modal-links'>
                                {links.map((l, i) => {
                                    const href = typeof l === ADVISORY_VALUE_TYPE_STRING ? l : l?.url || l?.href || l?.link || '';
                                    const label = typeof l === ADVISORY_VALUE_TYPE_STRING ? l : getLocalizedAdvisoryValue(l?.label || l?.title || l?.text || l?.displayName, t, i18n) || href;
                                    if (!href || !label) {
                                        return null;
                                    }
                                    return (
                                        <a key={`${href}-${i}`} href={href} target='_blank' rel='noopener noreferrer' className='caution-modal-link'>
                                            {label}
                                        </a>
                                    );
                                })}
                            </div>
                        }
                    </div>
                </GeneralDialog>
            }
        </>
    );
};

CautionBadge.propTypes = {
    advisory: PropTypes.shape({
        level: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        summary: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        knownLimitations: PropTypes.shape({
            displayNames: PropTypes.oneOfType([PropTypes.array, PropTypes.object])
        }),
        links: PropTypes.array
    })
};

export default CautionBadge;
